import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useGlobalLoading } from 'src/components/GlobalLoading';
import { If } from 'src/components/If';
import { FullPageSpinner } from 'src/components/Spinner';
import { useUserGuiding } from 'src/lib/services/user-guiding';
import { useAuth } from 'src/services/Auth';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import { SessionWarningModal } from 'src/components/SessionWarningModal';
import { DEFAULT_SESSION_CONFIG } from 'src/services/Session';

let tokenRefreshed = false;

const AuthLayout = () => {
  const {
    mount,
    dismount,
    refresh,
    isInitialLoading,
    showSessionWarning,
    warningSecondsRemaining,
    handleExtendSession,
    handleLogoutFromWarning,
    // AuthLayout is the single owner of the session-timeout machinery: it's
    // mounted once for the whole app and it renders the SessionWarningModal.
    // No other useAuth() call site may pass this — see UseAuthOptions.
  } = useAuth({ manageSession: true });
  const { isGlobalLoading, setGlobalLoading } = useGlobalLoading();
  const location = useLocation();
  const userGuiding = useUserGuiding();

  const [currentLocation, setCurrentLocation] = useState(location.pathname);

  useEffect(() => {
    if (!tokenRefreshed) {
      // Add timeout to prevent infinite loading if API is unreachable (#2181)
      const AUTH_REFRESH_TIMEOUT = 10_000; // 10 seconds
      let timeoutId: ReturnType<typeof setTimeout> | undefined;

      const refreshWithTimeout = Promise.race([
        refresh(),
        new Promise<void>((resolve) => {
          timeoutId = setTimeout(() => {
            console.warn('[AuthLayout] Auth refresh timed out after 10s, proceeding without refresh');
            resolve();
          }, AUTH_REFRESH_TIMEOUT);
        }),
      ]);

      refreshWithTimeout
        .catch((error) => {
          console.error('[AuthLayout] Auth refresh failed:', error);
        })
        .finally(() => {
          if (timeoutId) clearTimeout(timeoutId);
          setGlobalLoading(false);
        });

      tokenRefreshed = true;
    }

    mount();
    return () => {
      dismount();
    };
  }, []);

  // Track page views for UserGuiding
  useEffect(() => {
    if (currentLocation !== location.pathname) {
      setCurrentLocation(location.pathname);
      userGuiding.pageCapture();
    }
  }, [location.pathname]);

  return (
    <>
      <If
        when={isGlobalLoading || isInitialLoading}
        else={
          <QueryParamProvider adapter={ReactRouter6Adapter}>
            <Outlet />
          </QueryParamProvider>
        }
      >
        <FullPageSpinner />
      </If>
      <SessionWarningModal
        isOpen={showSessionWarning}
        secondsRemaining={warningSecondsRemaining}
        totalWarningSeconds={DEFAULT_SESSION_CONFIG.warningDurationMinutes * 60}
        onExtend={handleExtendSession}
        onLogout={handleLogoutFromWarning}
        onClose={handleExtendSession}
      />
    </>
  );
};

export default AuthLayout;
